/* RESPONSIBLE TEAM: team-reporting */
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
// @ts-ignore - @intercom/pulse/lib/sanitize has no type declarations
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import type { SafeString } from '@ember/template/-private/handlebars';
import {
  buildChartContextItem,
  buildReportContextItem,
  type ContextItem,
} from 'embercom/lib/reporting/custom/chart-context-serializer';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

const MAX_CHART_CONTEXTS = 5;
const SIDEBAR_WIDTH_STORAGE_KEY = 'reporting-agent-sidebar-width';
const DEFAULT_WIDTH = 33;
const MIN_WIDTH = 25;
const MAX_WIDTH = 60;

export default class ReportingAgentSidebarService extends Service {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  @tracked isOpen = false;
  @tracked chartContexts: ContextItem[] = [];
  @tracked lastChatId: string | null = null;
  @tracked width: number = this.loadWidth();
  @tracked isResizing = false;

  private unsubscribers: Array<() => void> = [];
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private containerElement: HTMLElement | null = null;

  init() {
    super.init();
    this.unsubscribers.push(
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent('reporting-agent:close-sidebar', () =>
        this.close(),
      ),
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'reporting-agent:remove-chart',
        (data: Record<string, unknown>) => {
          let index = data['index'] as number;
          this.removeChart(index);
        },
      ),
      this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        'reporting-agent:chat-id-changed',
        (data: Record<string, unknown>) => {
          this.lastChatId = data['chatId'] as string;
        },
      ),
    );
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribers.forEach((unsub) => unsub());
    window.removeEventListener('mousemove', this.onResize);
    window.removeEventListener('mouseup', this.endResize);
  }

  get sidebarStyle(): SafeString {
    let widthValue = this.isOpen ? `${this.width}%` : `${0}`;
    return sanitizeHtml(`width: ${widthValue}`);
  }

  get sidebarTransitionClass(): string {
    if (this.isResizing) {
      return '';
    }
    return 'transition-all duration-300 ease-in-out';
  }

  open() {
    this.chartContexts = [];
    this.isOpen = true;
  }

  openWithChart(chart: any, reportState: any) {
    let item = buildChartContextItem(chart, reportState);
    this.chartContexts = [item];
    this.isOpen = true;
  }

  openWithReport(report: any, reportState: any) {
    let item = buildReportContextItem(report, reportState);
    this.chartContexts = [item];
    this.isOpen = true;
  }

  addChart(chart: any, reportState: any) {
    if (this.chartContexts.length >= MAX_CHART_CONTEXTS) {
      return;
    }
    let item = buildChartContextItem(chart, reportState);
    this.chartContexts = [...this.chartContexts, item];
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('reporting-agent:add-chart', {
      chart: item,
    });
  }

  removeChart(index: number) {
    this.chartContexts = this.chartContexts.filter((_, i) => i !== index);
    if (this.chartContexts.length === 0) {
      this.close();
    }
  }

  close() {
    this.isOpen = false;
    this.chartContexts = [];
  }

  setContainerElement = (element: HTMLElement) => {
    this.containerElement = element;
  };

  startResize = (event: MouseEvent) => {
    event.preventDefault();
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.width;
    window.addEventListener('mousemove', this.onResize, { passive: true });
    window.addEventListener('mouseup', this.endResize);
  };

  onResize = (event: MouseEvent) => {
    let containerWidth = this.containerElement?.clientWidth ?? window.innerWidth;
    let deltaX = this.resizeStartX - event.clientX;
    let deltaPercent = (deltaX / containerWidth) * 100;
    let newWidth = this.resizeStartWidth + deltaPercent;
    this.width = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, newWidth));
  };

  endResize = () => {
    this.isResizing = false;
    this.saveWidth();
    window.removeEventListener('mousemove', this.onResize);
    window.removeEventListener('mouseup', this.endResize);
  };

  private loadWidth(): number {
    let stored = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    if (stored) {
      let parsed = Number(stored);
      if (!isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
        return parsed;
      }
    }
    return DEFAULT_WIDTH;
  }

  private saveWidth(): void {
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(Math.round(this.width)));
  }
}

declare module '@ember/service' {
  interface Registry {
    'reporting-agent-sidebar': ReportingAgentSidebarService;
    reportingAgentSidebar: ReportingAgentSidebarService;
  }
}
